import { filter, map, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { DoodModalModel } from '@core/models/modal.model';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalStoreDispatcher } from '@common/dispatchers/modal.dispatcher';
import {
  ConfigKeys,
  GetTargetConfigByHandle,
  ModalIOConstraints,
  ModalIOConstraintsDefault,
} from './modals-util.type';

//todoR A améliorer/discuter : distinguer des inputs requises pour l'appel / input venant du content builder ne pouvant pas être mdifié et peuvent être surchargé ?? (Est-ce que ça suffit de les mettre en optionnelles ou non ?)

//   ^?
/**
  * Usage example:
  *
  * 1. Define the modal's inputs and outputs: (And check GetModalInterface to implements the inputs in the modal)
  * ```typescript
export type MyCustomModalConfig = ModalConfig<{
  handle: 'my-custom-modal';
  inputs: {message?: string, title: string};
  outputs: { type: 'confirm' | 'cancel'; };
}>;
 *
 * ```
 *
 * 2. Emit an event:
 * ```typescript
 * this.modalsService.emitEvent({ type: 'confirm' });
 * ```
 *
 * 3. Retrieve the input data:
 * ```typescript
 * data$ = this.modalsService.getData('my-custom-modal');
 * ```
 *
 * 4. From the component that opens the modal, inject `ModalsService`, and pass the modal config:
 * ```typescript
 * private readonly modalsService: ModalsService<MyCustomModalConfig>;
 * ```
 *
 * 5. Listen to the events:
 * ```typescript
 * event$ = this.modalsService.event$(handle);
 * ```
 *
 * 6. Handle multiple modals, you can pass an array of modal configs:
 * ```typescript
 * private readonly modalsService: ModalsService<[MyCustomModalConfig1, MyCustomModalConfig2]>;
 * ```
 *
 * 7. Handle a modal that does not have a specific config:
 * ```typescript
 * this.modalsService.open<UntypedModalIO>(NonTypedModal.handle, undefined, {
*   // non-typed inputs
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ModalsService<
  MainModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = ModalIOConstraintsDefault,
> {
  private subjectSource = new Subject<{
    handle: string;
    event: Record<string, unknown> | undefined;
  }>();
  /**
   * Listen to the open modal events.
   * Limitation if the same modal (same handle) is opened multiple times, the event will be emitted
   * for all of them.
   */
  _event$ = (handle: string) =>
    this.subjectSource.pipe(
      filter(({ handle: h }) => h === handle),
      map(({ event }) => event),
    );

  event$<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >(handle: Handle): Observable<TargetConfig['outputs']> {
    return this._event$(handle) as unknown as Observable<TargetConfig['outputs']>;
  }

  constructor(
    private modalSelector: ModalStoreSelector,
    private modalDispatcher: ModalStoreDispatcher,
  ) {}

  /**
   * If the target modal has not the ModalIOConfig declare yet, you can call this method by passing UntypedModalIO to the type parameter.
   * ```typescript
   *this.modalsService.open<UntypedModalIO>(ItemEditModalComponent.handle);
   ```
   */
  open<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >(handle: Handle, attribute?: string, data?: TargetConfig['inputs']): void {
    const allModals = this.modalSelector.modals;
    const lastModal = allModals[0];
    this.modalDispatcher.addModal({
      handle,
      attribute,
      data,
      index: ((lastModal?.index ? lastModal.index / 10 : 0) + 1) * 10,
    });
  }

  close<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >(handle: Handle): void {
    this.modalDispatcher.removeModal(handle);
  }

  isModalLink(link: string): boolean {
    return link.startsWith('modal://');
  }

  openFromLink(link: string): void {
    const urlWithHttp = link.replace('modal://', 'http://');
    const url = new URL(urlWithHttp);
    const params = new URLSearchParams(url.search);
    const inputs: { [key: string]: unknown } = {};
    params.forEach((value, key) => {
      inputs[key] = value;
    });
    const handle = url.host;
    this.open(handle as any, undefined, inputs as any);
  }

  setData<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >(handle: Handle, data: TargetConfig['inputs']): void {
    this.modalDispatcher.updateModal({
      id: handle,
      changes: { data },
    });
  }

  getData<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >(handle: Handle): Observable<DoodModalModel<TargetConfig['inputs']> | undefined> {
    return this.modalSelector.selectModal(handle);
  }

  emitEvent<
    ModalIOConfig extends ModalIOConstraints | ModalIOConstraints[] = MainModalIOConfig,
    const Handle extends ConfigKeys<ModalIOConfig> = ConfigKeys<ModalIOConfig>,
    const TargetConfig extends ModalIOConstraints = GetTargetConfigByHandle<ModalIOConfig, Handle>,
  >({ data, handle }: { data: TargetConfig['outputs']; handle: Handle }): void {
    this.subjectSource.next({ handle, event: data });
  }
}
